import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { InvoiceBalanceService } from '@manager-app/service/invoice-blalance.service';
import { HxAuthService, HxUiLabelPipe, InvoiceType, PermissionKey, StoreProductModel, toFixed } from 'hx-services';
import { HxToastrService } from 'hx-component';
import { map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

const DISCARD_ASKED_MS = 30_000;
/** The answers «is the photo of a write-off required in this store» for the last half minute: a list of products asks the same store once, not once per product. */
const discardAsked = new Map<number | undefined, { at: number; answer: Promise<boolean> }>();

@Component({
    selector: 'app-store-product',
    templateUrl: './store-product.component.html',
    styleUrls: ['./store-product.component.css'],
    imports: [NgIf, FormsModule, NgbTooltip, TranslocoPipe, HxUiLabelPipe]
})
export class StoreProductComponent implements OnInit, OnDestroy, OnChanges {
  @Input() product: StoreProductModel;
  @Input() showDiscardValueInput = false;
  @Input() showDiscardBtn = false;
  @Input() invoiceType: InvoiceType;
  /** The store the write-off is made in; when it is not given, the store of the product is taken. */
  @Input() storeId?: number;
  hasDiscardProduct: boolean;
  discardValue: number;
  remainderAmount: number;
  currentAmount: number;
  Inventory: InvoiceType = InvoiceType.INVENTORY;
  positiveValueTypes: InvoiceType[] = [InvoiceType.DECOMMISSION, InvoiceType.DECOMMISSION_PRODUCT, InvoiceType.CONSUMPTION];
  private sub: Subscription;

  constructor(
    private invoiceBalanceService: InvoiceBalanceService,
    private auth: HxAuthService,
    private toastr: HxToastrService,
    private tr: TranslocoService,
  ) {
  }

  ngOnInit() {
    this.askDiscardPermission(this.storeId ?? this.product?.store?.id).then(has => {
      this.hasDiscardProduct = has;
      this.discardValue = this.hasDiscardProduct ? 1 : 0;
    });
    this.sub = this.invoiceBalanceService.balanceObs
      .pipe(map(discardMap => discardMap.get(this.product.productInfo.id)))
      .subscribe(value => {
        this.discardValue = value;
        this.remainderAmount = toFixed(this.invoiceType === InvoiceType.INVENTORY
          ? this.product.amount + this.discardValue
          : this.product.amount - this.discardValue);
      });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoiceType']) {
      if (this.hasDiscardProduct) {
        this.discardValue = 1;
        this.currentAmount = this.product.amount + 1;
      } else if (!this.hasDiscardProduct) {
        this.discardValue = 0;
        this.currentAmount = this.product.amount;
      }
      this.calculate();
    }
  }

  /** The right «обязательное фото при списании» is asked for the store the write-off is made in (`has-permission` with the store), not «anywhere». */
  private askDiscardPermission(storeId?: number): Promise<boolean> {
    const known = discardAsked.get(storeId);
    if (known && Date.now() - known.at < DISCARD_ASKED_MS) {
      return known.answer;
    }
    const answer = this.auth.hasPermission(PermissionKey.discard_product_input, storeId).then(result => result.hasPermission);
    discardAsked.set(storeId, { at: Date.now(), answer });
    answer.catch(() => discardAsked.delete(storeId));
    return answer;
  }

  addDiscardAmount() {
    this.invoiceBalanceService.updateDiscardAmount(this.product, toFixed(this.discardValue + 0.25));
  }

  subtractDiscardAmount() {
    const value = toFixed(this.discardValue - 0.25);
    if (this.product.amount - Math.abs(value) >= 0) {
      this.invoiceBalanceService.updateDiscardAmount(this.product, value);
    }
  }

  discard() {
    this.invoiceBalanceService.updateDiscardAmount(this.product, this.discardValue);
  }

  onDiscardValueChanged() {
    this.calculate();
  }

  private calculate() {
    if (!isNaN(Number(String(this.discardValue)))) {
      if (this.hasDiscardProduct) {
        if (this.invoiceType !== InvoiceType.INVENTORY) {
          if (!isNaN(Number(this.discardValue))) {
            this.currentAmount = toFixed(this.product.amount - this.discardValue);
          }
          if (this.discardValue <= 0) {
            this.toastr.error(this.tr.translate('store-product.greaterThenZero'));
            this.currentAmount = this.product.amount - 1;
            this.discardValue = 1;
          }
          if (this.currentAmount < 0) {
            this.toastr.error(this.tr.translate('store-product.balanceGreaterThenzero'));
            this.currentAmount = this.product.amount - 1;
            this.discardValue = 1;
          }
        } else {
          this.currentAmount = toFixed(this.product.amount + this.discardValue);
          // if (this.discardValue === 0) {
          //   this.toastr.error(this.tr.translate('store-product.greaterOrLessThenZero'));
          //   this.currentAmount = this.product.amount + 1;
          //   this.discardValue = 1;
          // }
          if (this.currentAmount < 0) {
            this.toastr.error(this.tr.translate('store-product.balanceGreaterThenzero'));
            this.currentAmount = this.product.amount + 1;
            this.discardValue = 1;
          }
        }
      }
      this.invoiceBalanceService.updateDiscardAmount(this.product, this.discardValue);
    }
  }
}

